import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ApiService } from '../../../../api.service';
import { VIDEO_STEP_TOKEN_HEADER, VideoInterviewStepTokenService } from './video-interview-step-token.service';

/** Mirrors com.aquent.domain.VideoInterviewStatus, whose names are what the wire carries. */
export type VideoInterviewStatus =
  'PENDING' | 'INVITED' | 'IN_PROGRESS' | 'COMPLETED' | 'INGESTED' | 'EXPIRED' | 'DELETED' | 'FAILED';

/**
 * The answer both endpoints give, deliberately vendor-neutral. A null status means the talent never
 * had an interview or the read failed; the two are indistinguishable here and both defer.
 */
export interface VideoInterviewResponse {
  status: VideoInterviewStatus | null;
  interviewId: string | null;
  /** The page to load in the iframe. */
  embedUrl: string | null;
  /** The same page, for the "open in a new tab" escape hatch. */
  externalUrl: string | null;
  completedAt: string | null;
}

/** Ingested is Completed plus results we have pulled. */
const ALREADY_RECORDED: VideoInterviewStatus[] = ['COMPLETED', 'INGESTED'];

/**
 * One predicate for both the skip and what the page shows, so the two cannot disagree. Also the one
 * place to narrow to a completion window, which `completedAt` on the same response carries.
 */
export function hasAlreadyRecorded(response: VideoInterviewResponse): boolean {
  return response?.status != null && ALREADY_RECORDED.includes(response.status);
}

/**
 * Calls talent-app's video interview proxy. Both endpoints answer 200 for every talent-caused
 * outcome, a vendor outage included, so callers branch on `status`, not on HTTP failure.
 *
 * The exception is identity (BIZ-40996). These are the only two funnel calls that will not take a
 * person id on trust: the proxy binds the talent to a credential the server signed and answers an
 * empty 401 or 403 when none vouches for the person being asked about. Both are HTTP failures, so
 * both reach the caller's error handler, where they land on the same deferred experience as an
 * unreachable service.
 *
 * <p>Three credentials can reach it, and the precedence is worth stating exactly, because it is not
 * "all three always travel".
 *
 * <p>The step token is attached below, on its own header, so it is unaffected by the rest. The other
 * two share the `Authorization` header and therefore cannot both travel:
 * `authenticated: true` has `ApiService` add the portal `Token …` credential of a talent who arrived
 * from MyAquent (only when one is actually held, so it costs a funnel-only applicant nothing), and
 * then `TalentHappyPathTokenInterceptorService` replaces `Authorization` outright with the funnel's
 * `Bearer …` whenever a funnel sign-in exists. So the portal credential reaches the server only when
 * there is no funnel sign-in — which is exactly the case it was added for — and the funnel token
 * wins whenever both are held.
 *
 * <p>That is coherent rather than lossy: it matches the server's own precedence, where a positive
 * person id on the funnel token outranks anything else. The one combination it would not serve is a
 * funnel token whose id is still -1 held alongside a portal token and no step token, and that is
 * unreachable — the video step is only entered through quick-apply or a gather, and both of those
 * supply a binding credential of their own.
 */
@Injectable({ providedIn: 'root' })
export class VideoInterviewApiService {

  constructor(private apiService: ApiService,
              private stepTokenService: VideoInterviewStepTokenService) {}

  /** Idempotent: a repeat call returns the same interview, so a refresh is safe. */
  provision(personId: number, orderId?: number): Observable<VideoInterviewResponse> {
    let params = new HttpParams().set('personId', String(personId));
    if (orderId != null) {
      params = params.set('orderId', String(orderId));
    }
    return this.apiService.httpWrapper<VideoInterviewResponse>({
      method: 'POST',
      url: '/api/video-interview/provision',
      authenticated: true,
      headers: this.stepTokenHeaders(),
      params
    });
  }

  /** Reads where the talent's interview has got to, without creating one. */
  readStatus(personId: number): Observable<VideoInterviewResponse> {
    const params = new HttpParams().set('personId', String(personId));
    return this.apiService.httpWrapper<VideoInterviewResponse>({
      method: 'GET',
      url: '/api/video-interview/status',
      authenticated: true,
      headers: this.stepTokenHeaders(),
      params
    });
  }

  /**
   * The step token, when this visit was given one. Its own header, never `Authorization`: it is not
   * a login and must not be offered as one. Absent for a logged-in talent, whose JWT already
   * carries a person id the server derived itself.
   */
  private stepTokenHeaders(): HttpHeaders | undefined {
    const stepToken = this.stepTokenService.token;
    return stepToken == null ? undefined : new HttpHeaders().set(VIDEO_STEP_TOKEN_HEADER, stepToken);
  }
}
